import { Divider, Grid, InputLabel, Stack } from "~/common/mui";
import SearchInput from "~/common/components/SearchInput";
import { UnitModeToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { FilterButton } from "~/features/search/components/common/mobileChips/FilterButton";
import { TechCategoryChip } from "~/features/search/components/common/mobileChips/TechCategoryChip";
import { ViewChip } from "~/features/search/components/common/mobileChips/ViewChip";
import { SearchDisplayMode } from "~/features/search/models/models";
import ReduxSortingPicker from "../../common/ReduxSortingPicker";
import { DisplayModeButtonList } from "../../common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import type { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleMissingSortAfterTabChangeThunk, updateSearchText } from "~/features/search/redux/actions";
import { trackTreesInteraction } from "~/app/analytics/ga/ga";
import { selectSearchQuery } from "~/features/search/redux/searchSelectors";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterSortChip } from "../../common/mobileChips/FilterSortChip";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

interface TopFilterBarProps {
  unitMode: UnitMode;
  displayMode: SearchDisplayMode;
  setUnitMode: (unitMode: UnitMode) => void;
  setDisplayMode: (displayMode: SearchDisplayMode) => void;
}

export const TopFilterBar = ({ unitMode, displayMode, setUnitMode, setDisplayMode }: TopFilterBarProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const screenSize = useNumericScreenSize();
  const pageType = useSearchPageType();

  const showTreeButton = pageType !== SearchPageType.Campaign;
  const showQueryInput = pageType === SearchPageType.Campaign;
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";

  const showChangeViewToggle = pageType !== SearchPageType.Campaign;
  const showSortBy = displayMode === SearchDisplayMode.LIST;

  const text = useSelector(selectSearchQuery);
  useEffect(() => {
    setSearchQuery(text);
  }, [text]);
  const [searchQuery, setSearchQuery] = useState(text);

  const showFilterButton = screenSize === useNumericScreenSize.desktopS && displayMode === SearchDisplayMode.TABULAR;

  let bar;
  if (screenSize <= useNumericScreenSize.tablet) {
    bar = (
      <>
        <Stack
          direction="row"
          sx={{
            gap: 1,
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          <ViewChip
            initialUnit={unitMode}
            initialViewMode={displayMode}
            applyValues={(unit, mode) => {
              setUnitMode(unit);
              setDisplayMode(mode);
            }}
          />
          <Divider flexItem orientation="vertical" />

          <FilterSortChip />

          <TechCategoryChip index={0} />
          <TechCategoryChip index={1} />
        </Stack>
      </>
    );
  } else {
    const funcDisplayMode = (targetDisplayMode: SearchDisplayMode) => {
      let display: "List" | "Tile" | "Tree";
      if (targetDisplayMode === SearchDisplayMode.LIST) {
        display = "Tile";
        dispatch(handleMissingSortAfterTabChangeThunk());
      } else if (targetDisplayMode === SearchDisplayMode.TABULAR) {
        display = "List";
      } else {
        display = "Tree";
      }
      trackTreesInteraction(gaInteractionType, display);
      setDisplayMode(targetDisplayMode);
    };

    bar = (
      <>
        <Grid container columns={9} gap={3}>
          {showFilterButton && (
            <Grid size={1.5} sx={{ display: "flex", flexFlow: "column", justifyContent: "flex-end" }}>
              <FilterButton fullWidth />
            </Grid>
          )}

          {showChangeViewToggle && (
            <Grid size={3}>
              <DisplayModeButtonList
                displayMode={displayMode}
                setDisplayMode={funcDisplayMode}
                showSeriesTree={showTreeButton}
              />
            </Grid>
          )}

          <Grid size={3}>
            <UnitModeToggle
              className={"scha__srp__action-bar__unit-toggle"}
              value={unitMode}
              onChange={setUnitMode}
              fullWidth={true}
            />
          </Grid>

          {showSortBy && (
            <Grid size={3} sx={{ ml: "auto" }}>
              {/* <Box> */}
              <InputLabel shrink>{t("searchPage.sort.title")}</InputLabel>
              <ReduxSortingPicker />
              {/* </Box> */}
            </Grid>
          )}
        </Grid>
      </>
    );
  }

  const submit = () => {
    dispatch(updateSearchText(searchQuery));
  };

  return (
    <>
      {bar}
      {showQueryInput && (
        <SearchInput
          onSearch={submit}
          label={t("searchPage.searchBox.placeholder")}
          onChange={setSearchQuery}
          value={searchQuery}
          sx={{ width: "354px" }}
          size="small"
        />
      )}
    </>
  );
};
